//----- Component --------------------------------------------------------/
// Developer     : Guillermo Sedran
// Creation Date : 26/03/2025
// Version       : 1
// Description   : Menú para configurar las coincidencias entre las tablas de la base de datos y las tablas de los archivos según el proveedor elegido
//
//------Changes --------------------------------------------------------------------- -------------//
// Change Date   :
// Change by     :
// Change description:
//
// Change Date   :
// Change by     :
// Change description:
//
//========================================================================/

import React, { useState, useEffect, useRef } from "react";
import { UrlApi } from "../../../services/apirest";
import {
  Box,
  Typography,
  Button,
  Stack,
  DialogTitle,
  DialogContent,
  FormControlLabel,
  Checkbox,
  TextField,
  IconButton,
  InputAdornment,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { read, utils } from "xlsx";
import ContentCopy from "@mui/icons-material/ContentCopy";
import ContentPaste from "@mui/icons-material/ContentPaste";

// ICONOS
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

// Notification
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function ConfiguracionReporte(props) {
  const [config, setConfig] = useState({
    id_proveedor: props.proveedor.id,
    fecha: "",
    hora: "",
    patente: "",
    producto: "",
    cantidad: "",
    nro_comprobante: "",
    fecha_hora_combinada: 0,
    formato_fecha: "DMY",
  });

  //Notification
  const [openSnack, setOpenSnack] = React.useState(false);
  const [colormensaje, setColorMensaje] = useState("");
  const [mensaje, setMensaje] = useState("");

  // Estados para el snackbar de copiado
  const [openCopySnack, setOpenCopySnack] = useState(false);
  const [copyMensaje, setCopyMensaje] = useState("");

  //*************************************************************************************************************************************/

  // DESHABILITAR BOTON AL GUARDAR
  const [disBtn, setDisBtn] = useState(false);

  //*************************************************************************************************************************************/

  // OBTENCION DE LOS DATOS EXISTENTES EN CASO DE QUE YA EXISTAN, SINO DATOS PREDETERMINADOS
  useEffect(() => {
    if (!props.proveedor.id) return;

    fetch(
      `${UrlApi}/proveedores_configuracion_reporte/proveedor/${props.proveedor.id}`
    )
      .then((res) => res.json())
      .then((data) => {
        const configData = data[0] || {}; // Tomar primer elemento si es array
        setConfig({
          id_configuracion: configData.id_configuracion || null,
          id_proveedor: configData.id_proveedor || props.proveedor.id,
          fecha_hora_combinada: configData.fecha_hora_combinada || 0,
          formato_fecha: configData.formato_fecha || "DMY",
          fecha: configData.fecha || "",
          hora: configData.hora || "",
          patente: configData.patente || "",
          producto: configData.producto || "",
          cantidad: configData.cantidad || "",
          nro_comprobante: configData.nro_comprobante || "",
        });
      })
      .catch((error) => {
        console.error("Error:", error);
        // Valores por defecto
        setConfig((prev) => ({
          ...prev,
          id_proveedor: props.proveedor.id,
        }));
      });
  }, [props.proveedor.id]);

  // ---------------------------------------------------------------------------

  // Esta función actualiza los TextField, el Checkbox y el Select
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setConfig((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
    }));
  };

  // ---------------------------------------------------------------------------

  //Función GUARDAR
  const handleGuardar = async (e) => {
    setDisBtn(true);
    e.preventDefault();

    const payload = {
      id_proveedor: props.proveedor.id,
      formato_fecha: config.formato_fecha,
      fecha_hora_combinada: config.fecha_hora_combinada,
      fecha: config.fecha,
      hora: config.fecha_hora_combinada ? null : config.hora,
      patente: config.patente,
      producto: config.producto,
      cantidad: config.cantidad,
      nro_comprobante: config.nro_comprobante,
    };

    // Determinar si es creación o actualización
    const metodo = config.id_configuracion ? "PUT" : "POST";

    // Usar id_configuracion para PUT
    const url = config.id_configuracion
      ? `${UrlApi}/proveedores_configuracion_reporte/${config.id_configuracion}`
      : `${UrlApi}/proveedores_configuracion_reporte`;

    try {
      const response = await fetch(url, {
        method: metodo,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(
          response.statusText || "Error en la respuesta del servidor"
        );
      }

      const data = await response.json();

      // Éxito - Mostrar notificación
      setOpenSnack(true);
      setColorMensaje("#D4EFDF");
      setMensaje("Configuración guardada exitosamente!");

      // Actualizar el estado con la nueva configuración
      if (!config.id_configuracion && data.id_configuracion) {
        setConfig((prev) => ({
          ...prev,
          id_configuracion: data.id_configuracion,
        }));
      }
    } catch (error) {
      console.error("Error al guardar:", error);
      setOpenSnack(true);
      setColorMensaje("#F1948A");
      setMensaje("Error al guardar la configuración");
    }
  };

  //  ------------------------------------------------------------------------

  // Cierre de la notificación
  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnack(false);
    setDisBtn(false);

    // Acciones de cierre de diálogo y actualización después de que el Snack se cierre
    props.onProveedorGuardado(); // Función para obtener valores de nuevo en el componente padre
    props.handleCloseConfiguracionReporte();
  };

  // Cierre de la notificación de copiado
  const handleCloseCopySnack = (event, reason) => {
    if (reason === "clickaway") return;
    setOpenCopySnack(false);
  };

  //  ------------------------------------------------------------------------

  // Función para copiar al portapapeles
  const handleCopyHeader = (headerName) => {
    navigator.clipboard.writeText(headerName);
    setOpenCopySnack(true);
    setCopyMensaje(`"${headerName}" copiado al portapapeles`);
  };

  const handlePaste = async (fieldName) => {
    try {
      const text = await navigator.clipboard.readText();
      handleChange({
        target: {
          name: fieldName,
          value: text,
        },
      });
    } catch (error) {
      console.error("Error al pegar:", error);
    }
  };

  // Tabla para los excels
  const [excelHeaders, setExcelHeaders] = useState([]);
  const [archivoCargado, setArchivoCargado] = useState(null);
  const fileInputRef = useRef(null);

  // Función para leer el Excel
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setArchivoCargado(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = read(data, { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = utils.sheet_to_json(worksheet, { header: 1 });

      // Extraer solo los headers
      const headers = jsonData[0] || [];
      setExcelHeaders(headers);
    };
    reader.readAsArrayBuffer(file);
  };

  // Generar columnas dinámicamente
  const gridColumns = excelHeaders.map((header) => ({
    field: header,
    headerName: header,
    width: header.length * 10 + 40, // 🔹 Calcula el ancho en función del texto
    minWidth: 100, // 🔹 Mínimo para evitar cortes en textos pequeños
    maxWidth: 300, // 🔹 Máximo para evitar columnas demasiado anchas
    sortable: false,
    disableColumnMenu: true,
    renderHeader: (params) => (
      <div
        style={{ display: "flex", alignItems: "center", whiteSpace: "nowrap" }}
      >
        <span>{params.colDef.headerName}</span>
        <IconButton
          size="small"
          onClick={() => handleCopyHeader(params.colDef.headerName)}
          sx={{ ml: 1, p: 0.5 }}
        >
          <ContentCopy fontSize="small" />
        </IconButton>
      </div>
    ),
  }));

  return (
    <>
      <Box
        style={{
          borderWidth: 2,
          borderStyle: "solid",
          border: "2px solid #2B76A6",
        }}
      >
        <DialogTitle>
          <Grid container alignItems="center">
            <Grid item xs={6.5}>
              {/* Configuración de Reporte para {props.proveedor.proveedor} */}

              <Typography
                // variant="h6"
                color="white"
                sx={{
                  display: "inline-block",
                  backgroundColor: "#2B76A6",
                  // borderBottomRightRadius: "12px",
                  borderRadius: "16px",
                  padding: "8px 18px",
                  zIndex: 1,
                }}
              >
                <b>Configuración de Reporte para {props.proveedor.proveedor}</b>
              </Typography>
            </Grid>
            <Grid item xs={0.5}>
              <Tooltip
                arrow
                placement="top"
                title="Cargue un archivo para obtener una vista previa de sus columnas"
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: "offset",
                        options: {
                          offset: [0, -14],
                        },
                      },
                    ],
                  },
                }}
              >
                <IconButton>
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid item xs={5}>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept=".xlsx, .ods"
                onChange={handleFileUpload}
              />
              <Button
                variant="contained"
                color="secondary"
                fullWidth
                onClick={() => fileInputRef.current.click()}
              >
                {archivoCargado ? "Cambiar Archivo" : "Cargar Archivo"}
              </Button>
            </Grid>

          
          </Grid>

          <Grid container alignItems="center" >
  <Grid item xs={12}>
              {archivoCargado && (
                <Typography variant="body2" sx={{ fontStyle: "italic", marginTop: "12px", marginBottom: "-6px" }}>
                  <b>Archivo:</b> {archivoCargado}
                </Typography>
              )}
            </Grid>
</Grid>

        </DialogTitle>

        <DialogContent>
          {/* DataGrid de headers */}
          <Box
            sx={{
              width: "100%",
              mb: 3,
              "& .MuiDataGrid-root": {
                overflow: "visible !important",
              },
              "& .MuiDataGrid-virtualScroller": {
                overflowX: "auto !important",
                overflowY: "hidden !important",
                minHeight: "9px !important", // Altura mínima (header + scrollbar)
                marginTop: "0 !important",
                paddingBottom: "0 !important", // Elimino el padding inferior
              },
              "& .MuiDataGrid-columnHeaders": {
                position: "sticky !important",
                left: 0,
                zIndex: 2,
                backgroundColor: "#f5f5f5",
                minHeight: "36px !important", // Altura de los headers
                maxHeight: "36px !important",
              },
              "& .MuiDataGrid-columnHeader": {
                padding: "6px !important",
              },
              "& .MuiDataGrid-virtualScrollerContent": {
                height: "0 !important",
                minHeight: "0 !important",
              },
              "& .MuiDataGrid-cell": {
                visibility: "hidden",
                height: 0,
                padding: 0,
                border: "none",
              },
              "& .MuiDataGrid-row": {
                maxHeight: "0 !important",
                minHeight: "0 !important",
              },
              // Scrollbar
              "& ::-webkit-scrollbar": {
                height: "10px !important",
              },
              "& ::-webkit-scrollbar-thumb": {
                backgroundColor: "#bdbdbd !important",
                borderRadius: "6px !important",
                minHeight: "30px", // Altura mínima del control deslizante
              },
              "& ::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#9e9e9e !important",
              },
              "& ::-webkit-scrollbar-track": {
                backgroundColor: "#f0f0f0 !important", // Fondo
              },
            }}
          >
            <DataGrid
              rows={[]}
              columns={gridColumns}
              density="compact"
              disableRowSelectionOnClick
              disableColumnSorting
              hideFooter
              autoHeight // Altura automática
              localeText={{
                noRowsLabel: "", // Texto vacío
                noResultsOverlayLabel: "",
              }}
              sx={{
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold !important",
                  whiteSpace: "nowrap", // Evitar wrap en headers
                },
                "& .MuiDataGrid-columnHeader": {
                  overflow: "visible !important",
                },
              }}
              slots={{
                noRowsOverlay: () => null, // Eliminar overlay
              }}
              slotProps={{
                row: { tabIndex: -1 },
                cell: { disablePortal: true },
              }}
            />
          </Box>

          <form onSubmit={handleGuardar}>
            <Box sx={{ mt: 1 }}>
              {/* Fecha y Hora */}
              <Box sx={{ display: "flex", flexDirection: "column", mb: 2 }}>
                <Grid container alignItems="flex-start" spacing={2}>
                  {/* Primera columna: Label + Flecha + Checkbox */}
                  <Grid
                    item
                    xs={4.5}
                    sx={{
                      display: "flex",
                      flexDirection: "row", // En fila, no columna
                      alignItems: "center", // Alineados verticalmente
                      justifyContent: "flex-start", // Alineado a la izquierda
                      mt: 2,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: "bold", mr: 1 }}
                    >
                      Fecha y Hora
                    </Typography>

                    {/* <Typography
                                      variant="body1"

                      color="white"
                      sx={{
                        display: "inline-block",
                        backgroundColor: "#2B76A6",
                        borderRadius: "16px",
                        padding: "4px 8px",
                        zIndex: 1,
                      }}
                    >
                      <b>Fecha y Hora</b>
                    </Typography> */}

                    <ArrowRightAltIcon sx={{ mx: 1, mr: 3 }} />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="fecha_hora_combinada"
                          checked={config.fecha_hora_combinada === 1}
                          onChange={handleChange}
                        />
                      }
                      label="Combinadas"
                    />
                  </Grid>

                  {/* Segunda columna: Inputs */}
                  <Grid item xs={7.5}>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {config.fecha_hora_combinada === 1 ? (
                        <TextField
                          sx={{ flex: 1 }}
                          label="Nombre columna"
                          name="fecha"
                          value={config.fecha}
                          onChange={handleChange}
                          autoComplete="off"
                          required
                          size="small"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => handlePaste("fecha")}
                                  edge="end"
                                  size="small"
                                  sx={{ mr: -1 }}
                                >
                                  <ContentPaste fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      ) : (
                        <>
                          <TextField
                            sx={{ flex: 1 }}
                            label="Fecha"
                            name="fecha"
                            value={config.fecha}
                            onChange={handleChange}
                            autoComplete="off"
                            required
                            size="small"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => handlePaste("fecha")}
                                    edge="end"
                                    size="small"
                                    sx={{ mr: -1 }}
                                  >
                                    <ContentPaste fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                          <TextField
                            sx={{ flex: 1 }}
                            label="Hora"
                            name="hora"
                            value={config.hora}
                            onChange={handleChange}
                            autoComplete="off"
                            required
                            size="small"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => handlePaste("hora")}
                                    edge="end"
                                    size="small"
                                    sx={{ mr: -1 }}
                                  >
                                    <ContentPaste fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </>
                      )}
                    </Box>

                    {/* Select para Formato Fecha */}
                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        justifyContent: "flex-start",
                      }}
                    >
                      <FormControl size="small" sx={{ width: "100%" }}>
                        <InputLabel id="formato-fecha-label">
                          Formato Fecha
                        </InputLabel>
                        <Select
                          labelId="formato-fecha-label"
                          label="Formato Fecha"
                          name="formato_fecha"
                          value={config.formato_fecha || ""}
                          onChange={handleChange}
                        >
                          <MenuItem value="DMY">Día / Mes / Año (DMY)</MenuItem>
                          <MenuItem value="MDY">Mes / Día / Año (MDY)</MenuItem>
                          <MenuItem value="YMD">Año / Mes / Día (YMD)</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Patente */}
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography
                  variant="body1"
                  sx={{ width: "25%", fontWeight: "bold" }}
                >
                  Patente
                </Typography>

                {/* <Typography
                                      variant="body1"
                      color="white"
                      sx={{
                        display: "inline-block",
                        backgroundColor: "#2B76A6",
                        borderRadius: "16px",
                        padding: "4px 8px",
                        zIndex: 1,
                      }}
                    >
                      <b>Patente</b>
                    </Typography> */}
                <ArrowRightAltIcon sx={{ mx: 2 }} />
                <TextField
                  sx={{ flex: 1 }}
                  label="Nombre columna"
                  name="patente"
                  value={config.patente}
                  onChange={handleChange}
                  autoComplete="off"
                  required
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handlePaste("patente")}
                          edge="end"
                          size="small"
                          sx={{ mr: -1 }}
                        >
                          <ContentPaste fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Producto */}
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography
                  variant="body1"
                  sx={{ width: "25%", fontWeight: "bold" }}
                >
                  Producto
                </Typography>
                <ArrowRightAltIcon sx={{ mx: 2 }} />
                <TextField
                  sx={{ flex: 1 }}
                  label="Nombre columna"
                  name="producto"
                  value={config.producto}
                  onChange={handleChange}
                  autoComplete="off"
                  required
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handlePaste("producto")}
                          edge="end"
                          size="small"
                          sx={{ mr: -1 }}
                        >
                          <ContentPaste fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Cantidad */}
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography
                  variant="body1"
                  sx={{ width: "25%", fontWeight: "bold" }}
                >
                  Cantidad
                </Typography>
                <ArrowRightAltIcon sx={{ mx: 2 }} />
                <TextField
                  sx={{ flex: 1 }}
                  label="Nombre columna"
                  name="cantidad"
                  value={config.cantidad}
                  onChange={handleChange}
                  autoComplete="off"
                  required
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handlePaste("cantidad")}
                          edge="end"
                          size="small"
                          sx={{ mr: -1 }}
                        >
                          <ContentPaste fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Comprobante */}
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography
                  variant="body1"
                  sx={{ width: "25%", fontWeight: "bold" }}
                >
                  N° Comprobante
                </Typography>
                <ArrowRightAltIcon sx={{ mx: 2 }} />
                <TextField
                  sx={{ flex: 1 }}
                  label="Nombre columna"
                  name="nro_comprobante"
                  value={config.nro_comprobante}
                  onChange={handleChange}
                  autoComplete="off"
                  required
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handlePaste("nro_comprobante")}
                          edge="end"
                          size="small"
                          sx={{ mr: -1 }}
                        >
                          <ContentPaste fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Botones */}
              <Grid
                container
                direction={{ xs: "column", sm: "row" }}
                marginTop={3}
              >
                <Grid item xs={9}>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={props.handleCloseConfiguracionReporte}
                    disabled={disBtn}
                  >
                    Volver
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={disBtn}
                  >
                    Guardar
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </form>
        </DialogContent>
      </Box>

      {/* Snackbar al Copiar un Titulo */}
      <Stack spacing={2} sx={{ width: "100%" }}>
        <Snackbar
          open={openCopySnack}
          autoHideDuration={2500}
          onClose={handleCloseCopySnack}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Alert
            onClose={handleCloseCopySnack}
            severity="info"
            variant="filled"
            sx={{
              backgroundColor: "#2196F3", // Azul
              color: "white",
              "& .MuiAlert-icon": { color: "white" },
            }}
          >
            {copyMensaje}
          </Alert>
        </Snackbar>
      </Stack>

      {/* Notificación al Guardar */}
      <Stack spacing={2} sx={{ width: "100%" }}>
        <Snackbar
          open={openSnack}
          autoHideDuration={5000}
          onClose={handleCloseSnack}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnack}
            severity="success"
            sx={{ width: "100%" }}
            style={{ backgroundColor: colormensaje, color: "black" }}
          >
            {mensaje}
          </Alert>
        </Snackbar>
      </Stack>
    </>
  );
}

export default ConfiguracionReporte;
